import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { selectAccess, selectToken, setAccess } from '../store/authSlice';
import { useMutations } from '../hooks/useApiMutation';

export const homePathForRole = (role) => (role === 'Client' ? '/portal' : '/');

/** Clients live under their own shell, so their profile screen has its own path. */
export const profilePathForRole = (role) =>
  (role === 'Client' ? '/portal/profile' : '/profile');

/**
 * Gate for every authenticated route.
 *
 * Beyond the token check it bootstraps the session: GET /auth/me is the single
 * source of truth for role, tenant and module grants, so the shell waits for it
 * before rendering anything that depends on entitlements. An account still holding
 * its temporary password is pushed to the change-password screen — the API refuses
 * everything else for it anyway.
 */
const ProtectedRoute = ({ children }) => {
  const token = useSelector(selectToken);
  const access = useSelector(selectAccess);
  const dispatch = useDispatch();
  const location = useLocation();
  const { mutate } = useMutations();
  const [resolving, setResolving] = useState(!access);
  const requestedRef = useRef(false);

  // Revalidated once per shell mount, even when a cached copy exists. The cache in
  // localStorage survives across sessions, so a grant revoked meanwhile would otherwise keep
  // its menu entries and dashboard tiles — the API refuses them and the user gets a wall of
  // 403 toasts instead of a screen that simply is not offered. The cached copy still renders
  // immediately; it is a head start, not the source of truth.
  useEffect(() => {
    if (!token || requestedRef.current) return;

    requestedRef.current = true;
    if (!access) setResolving(true);

    mutate('authMe', {
      method: 'get',
      endpoint: '/auth/me',
      onSuccess: (data) => dispatch(setAccess(data)),
    }).finally(() => setResolving(false));
  // `access` is deliberately absent: writing it back must not schedule another fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch, mutate]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (!access && resolving) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (access?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Deployment requires an authenticator this account has not enrolled. The API answers
  // 403 everywhere but the profile/2FA surface, so route there rather than let the user
  // walk into a wall of failures. Checked after the password gate: an account on a
  // temporary password has to clear that first.
  const profilePath = profilePathForRole(access?.role);
  if (access?.twoFactorSetupRequired && location.pathname !== profilePath) {
    return <Navigate to={profilePath} replace />;
  }

  return children ? children : <Outlet />;
};

export default ProtectedRoute;
